/**
 * Локализация веб-клиента: русский, английский, немецкий.
 *
 *   t('lobby.title')                    — строка словаря; {x} — подстановка, {x|cr} — сумма кредитов;
 *   tp('lobby.players', n)              — формы множественного числа через «|» в порядке категорий
 *                                         Intl.PluralRules языка (ru: one|few|many, en/de: one|other); {n} — само число;
 *   tm(msg, fallback)                   — сообщение сервера по коду (packages/protocol/src/messages.ts).
 *
 * Язык: ?lang= в адресе → сохранённый выбор → язык браузера → английский. Смена языка
 * перерисовывает приложение целиком (ui/App.tsx — ключ по языку), состояние игры не теряется:
 * оно живёт в net/client.ts.
 *
 * Словари: i18n/<язык>/<раздел>.ts. Русский — источник ключей; en и de типизированы им,
 * поэтому пропущенный или лишний ключ — ошибка компиляции. Термины — i18n/glossary.md.
 */
import { useSyncExternalStore } from 'react';
import { formatMsg, type MessageCatalog, type Msg } from '@mt/protocol';
import { ru, type Dict } from './ru';
import { en } from './en';
import { de } from './de';

export type Lang = 'ru' | 'en' | 'de';

export const LANGS: readonly { id: Lang; short: string; title: string; locale: string }[] = [
  { id: 'ru', short: 'RU', title: 'Русский', locale: 'ru-RU' },
  { id: 'en', short: 'EN', title: 'English', locale: 'en-GB' },
  { id: 'de', short: 'DE', title: 'Deutsch', locale: 'de-DE' },
];

const DICTS: Record<Lang, Dict> = { ru, en, de };
const STORAGE_KEY = 'mt.lang';
const isLang = (v: unknown): v is Lang => v === 'ru' || v === 'en' || v === 'de';

function detect(): Lang {
  try {
    const q = new URLSearchParams(location.search).get('lang');
    if (isLang(q)) return q;
  } catch {
    /* нет location (тесты) */
  }
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLang(saved)) return saved;
  } catch {
    /* хранилище недоступно */
  }
  for (const l of typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : []) {
    const base = l?.slice(0, 2).toLowerCase();
    if (isLang(base)) return base;
  }
  return 'en';
}

let current: Lang = detect();
const listeners = new Set<() => void>();
if (typeof document !== 'undefined') document.documentElement.lang = current;

export const getLang = (): Lang => current;
export const locale = (): string => LANGS.find((l) => l.id === current)!.locale;

export function setLang(l: Lang): void {
  if (l === current) return;
  current = l;
  try {
    localStorage.setItem(STORAGE_KEY, l);
  } catch {
    /* выбор не сохранится между визитами */
  }
  if (typeof document !== 'undefined') document.documentElement.lang = l;
  for (const f of listeners) f();
}

export function useLang(): Lang {
  return useSyncExternalStore(
    (f) => (listeners.add(f), () => listeners.delete(f)),
    getLang,
    getLang,
  );
}

// ───────────── ключи и подстановка ─────────────

type Ns = Exclude<keyof Dict, 'srv'>;
export type TKey = { [N in Ns]: `${N}.${Extract<keyof Dict[N], string>}` }[Ns];
export type TParams = Record<string, string | number>;

function raw(key: TKey): string {
  const dot = key.indexOf('.');
  const ns = key.slice(0, dot) as Ns;
  const k = key.slice(dot + 1);
  const d = DICTS[current][ns] as Record<string, string>;
  return d[k] ?? (ru[ns] as Record<string, string>)[k] ?? key;
}

function fill(s: string, params?: TParams): string {
  if (!params) return s;
  return s.replace(/\{(\w+)(\|cr)?\}/g, (whole, k: string, cr?: string) => {
    const v = params[k];
    if (v === undefined) return whole;
    return cr && typeof v === 'number' ? credits(v) : String(v);
  });
}

/** Строка словаря на текущем языке. */
export function t(key: TKey, params?: TParams): string {
  return fill(raw(key), params);
}

const PLURAL_ORDER: Record<Lang, Intl.LDMLPluralRule[]> = { ru: ['one', 'few', 'many', 'other'], en: ['one', 'other'], de: ['one', 'other'] };

/** Строка с формой множественного числа для n; {n} подставляется автоматически. */
export function tp(key: TKey, n: number, params?: TParams): string {
  const forms = raw(key).split('|');
  const cat = new Intl.PluralRules(locale()).select(n);
  const i = PLURAL_ORDER[current].indexOf(cat);
  const form = forms[Math.min(i < 0 ? forms.length - 1 : i, forms.length - 1)]!;
  return fill(form, { n: num(n, 0), ...params });
}

/** Сообщение сервера (ошибка, запись хронологии, причина отмены); без кода — текст сервера как есть. */
export function tm(m: Msg | null | undefined, fallback = ''): string {
  if (!m) return fallback;
  return formatMsg(DICTS[current].srv as MessageCatalog, m, credits);
}

// ───────────── числа, суммы, даты ─────────────

/** Число по правилам языка: num(1234.5, 2) → «1 234,50» / «1,234.50» / «1.234,50». */
export function num(x: number, digits = 2): string {
  return new Intl.NumberFormat(locale(), { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(x);
}

/** Сумма кредитов без единицы: минорные единицы → «10 000» или «12,50» (дробная часть — только если есть). */
export function creditsBare(minor: number): string {
  const abs = Math.abs(minor);
  const s = new Intl.NumberFormat(locale(), { minimumFractionDigits: abs % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(abs / 100);
  return (minor < 0 ? '−' : '') + s;
}

/** Сумма кредитов с единицей языка: «10 000 кр.», «10,000 cr», «10.000 Cr». */
export function credits(minor: number): string {
  return `${creditsBare(minor)} ${raw('common.creditsUnit')}`;
}

export function formatTime(t: number): string {
  return new Date(t).toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function formatDateTime(t: number): string {
  return new Date(t).toLocaleString(locale(), { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function formatDate(t: number): string {
  return new Date(t).toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
}
